import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import BusinessLink from "@/components/BusinessLink";
import MySitesSection from "@/components/MySitesSection";
import { motion } from "framer-motion";
import {
  MapPin, Star, Building2, FileText, Plus,
  ExternalLink, Sparkles, Menu, X, LogOut, User, Mail, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">lokalnie.pro</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/szukaj" className="text-sm font-medium text-muted-foreground hover:text-foreground">
              Katalog firm
            </Link>
            <Link to="/dashboard" className="text-sm font-medium text-foreground">
              Panel
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="flex items-center gap-2 p-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Wyloguj
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col gap-3">
              <Link to="/szukaj" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Katalog firm
              </Link>
              <Link to="/dashboard" className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                Panel
              </Link>
              <div className="flex items-center gap-2 py-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{user?.name}</span>
              </div>
              <Button variant="ghost" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                <LogOut className="w-4 h-4 mr-2" />
                Wyloguj
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default function DashboardPage() {
  const { user, resendVerification } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ total_businesses: 0, total_landing_pages: 0 });
  const [recentBusinesses, setRecentBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success("Wysłano ponownie — sprawdź skrzynkę");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się wysłać");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, businessesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/businesses?limit=5`)
        ]);
        setStats(statsRes.data);
        setRecentBusinesses(businessesRes.data.items || businessesRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user && user.email_verified === false && (
          <div className="mb-6 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 flex-shrink-0" />
              Potwierdź swój adres e-mail — sprawdź skrzynkę odbiorczą.
            </div>
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={handleResendVerification} disabled={resending}>
              {resending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Wyślij ponownie
            </Button>
          </div>
        )}

        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Witaj, {user?.name || 'Użytkowniku'}!
          </h1>
          <p className="text-slate-500 mt-1">
            Zarządzaj swoimi firmami i landing pages
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Firmy w katalogu</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_businesses}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Landing pages</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_landing_pages}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Kategorie</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.categories_count || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Miasta</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.cities_count || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-slate-900">
                    Wygeneruj landing page dla firmy
                  </h3>
                  <p className="text-slate-600 text-sm">
                    Znajdź firmę bez strony internetowej i pozwól AI stworzyć profesjonalną stronę
                  </p>
                </div>
                <Link to="/szukaj" data-testid="find-business-btn">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                    Znajdź firmę
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* My Sites — Faza H */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Moje strony</h2>
          </div>
          <MySitesSection />
        </motion.section>

        {/* Recent Businesses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Ostatnio dodane firmy</CardTitle>
              <Link to="/szukaj" data-testid="view-all-businesses-btn">
                <Button variant="ghost" size="sm">
                  Zobacz wszystkie
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 animate-pulse">
                      <div className="w-16 h-16 bg-slate-200 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-1/2" />
                        <div className="h-3 bg-slate-200 rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {recentBusinesses.map((business) => (
                    <BusinessLink
                      key={business.business_id}
                      business={business}
                      className="flex items-center gap-4 p-2 -mx-2 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`dashboard-business-${business.business_id}`}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={business.photos?.[0] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200'}
                          alt={business.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{business.name}</h4>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Badge variant="secondary" className="text-xs">{business.category}</Badge>
                          <span>•</span>
                          <span>{business.city}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{business.rating}</span>
                      </div>
                      {!business.website && (
                        <Badge className="bg-amber-100 text-amber-700 flex-shrink-0">
                          Bez strony
                        </Badge>
                      )}
                    </BusinessLink>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
